import { useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Mail, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/api";
import { RatePackage } from "@/types";
import { OSChrome } from "@/components/os/OSChrome";
import { OSWindow } from "@/components/os/Window";

const EMPTY = { name: "", email: "", project_need: "", socials: "", website: "" };

export default function InquiryPage() {
  const [popup, setPopup] = useState(true);
  const [intro, setIntro] = useState("hey! let's make something.");
  const [packages, setPackages] = useState<RatePackage[]>([]);
  const [form, setForm] = useState(EMPTY);
  const [plans, setPlans] = useState<string[]>([]);
  const [otherDetails, setOtherDetails] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get("/content/home").then((r) => {
      if (r.data?.inquiry_intro) setIntro(r.data.inquiry_intro);
    }).catch(() => {});
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);

  const set = (k: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const togglePlan = (label: string) =>
    setPlans((p) => (p.includes(label) ? p.filter((x) => x !== label) : [...p, label]));

  const planLabel = (p: RatePackage) => `${p.name} (${p.price_label})`;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await api.post("/inquiries", { ...form, plans, other_details: otherDetails });
      setSent(true);
      toast.success("TRANSMISSION COMPLETE");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  const PlanOption = ({ label }: { label: string }) => {
    const on = plans.includes(label);
    return (
      <label
        data-testid={`plan-option-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className={`flex cursor-pointer items-center gap-2.5 border-2 border-ink px-3 py-2 font-spacemono text-[11px] font-bold transition-all duration-100 ${
          on ? "translate-x-[1px] translate-y-[1px] bg-butter" : "bg-white shadow-hard-sm hover:bg-butter/50"
        }`}
      >
        <input type="checkbox" className="sr-only" checked={on} onChange={() => togglePlan(label)} />
        <span className={`flex h-4 w-4 shrink-0 items-center justify-center border-2 border-ink text-[10px] ${on ? "bg-ink text-butter" : "bg-white"}`} aria-hidden>
          {on ? "✓" : ""}
        </span>
        {label}
      </label>
    );
  };

  return (
    <OSChrome id="inquiry" title="compose.exe — new_project" tint="mint" maxW="max-w-2xl">
      <div className="p-5 sm:p-8">
        {sent ? (
          <div data-testid="inquiry-success" className="flex flex-col items-center py-8 text-center">
            <span className="flex h-16 w-16 items-center justify-center border-[3px] border-ink bg-mint shadow-hard">
              <CheckCircle2 size={30} />
            </span>
            <p className="mt-6 font-pixel text-base leading-relaxed sm:text-lg">MESSAGE TRANSMITTED</p>
            <p className="mt-4 max-w-sm text-sm font-medium text-ink/75">
              Your project brief just landed in our inbox — we'll get back to you ASAP! (Usually within 24–48 hours.)
            </p>
            <button data-testid="inquiry-again" onClick={() => { setForm(EMPTY); setPlans([]); setOtherDetails(""); setSent(false); }} className="btn-chunky mt-7 bg-white px-5 py-2.5 text-xs">
              SEND ANOTHER
            </button>
          </div>
        ) : (
          <form data-testid="inquiry-form" onSubmit={submit} className="flex flex-col gap-5">
            <p className="font-spacemono text-[11px] text-ink/60">
              C:\late_nights&gt; compose --to=latenights <span className="animate-blink">▌</span>
            </p>

            <div>
              <label htmlFor="inq-name" className="mb-1.5 block font-spacemono text-xs font-bold uppercase tracking-wider">name:</label>
              <input id="inq-name" data-testid="inquiry-name" required maxLength={120} value={form.name} onChange={set("name")} className="os-input" placeholder="YourNameHere" />
            </div>
            <div>
              <label htmlFor="inq-email" className="mb-1.5 block font-spacemono text-xs font-bold uppercase tracking-wider">email:</label>
              <input id="inq-email" data-testid="inquiry-email" type="email" required value={form.email} onChange={set("email")} className="os-input" placeholder="you@channel.com" />
            </div>

            <fieldset>
              <legend className="mb-1.5 font-spacemono text-xs font-bold uppercase tracking-wider">which plan(s) are you eyeing? <span className="font-normal text-ink/50">(pick any)</span></legend>
              <div className="flex flex-col gap-2">
                {packages.map((p) => <PlanOption key={p.id} label={planLabel(p)} />)}
                {packages.some((p) => p.kind === "custom" && plans.includes(planLabel(p))) && (
                  <textarea
                    data-testid="plan-other-details"
                    rows={3}
                    maxLength={1000}
                    value={otherDetails}
                    onChange={(e) => setOtherDetails(e.target.value)}
                    className="os-input resize-y"
                    placeholder="Whatcha thinking?"
                  />
                )}
              </div>
            </fieldset>

            <div>
              <label htmlFor="inq-need" className="mb-1.5 block font-spacemono text-xs font-bold uppercase tracking-wider">project details:</label>
              <textarea id="inq-need" data-testid="inquiry-need" required rows={5} maxLength={5000} value={form.project_need} onChange={set("project_need")} className="os-input resize-y" placeholder="Long form or short form? Posting frequency? Channel size, goals, deadlines — spill it." />
            </div>
            <div>
              <label htmlFor="inq-socials" className="mb-1.5 block font-spacemono text-xs font-bold uppercase tracking-wider">socials (optional):</label>
              <input id="inq-socials" data-testid="inquiry-socials" maxLength={500} value={form.socials} onChange={set("socials")} className="os-input" placeholder="youtube.com/@yourchannel, @yourhandle" />
            </div>

            <input
              className="hp-field"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={form.website}
              onChange={set("website")}
              name="website"
              data-testid="inquiry-honeypot"
            />

            <button data-testid="inquiry-submit" type="submit" disabled={sending} className="btn-chunky bg-lnpink px-6 py-3 text-sm disabled:opacity-60 sm:self-start">
              {sending ? "DIALING…" : "SEND"} <Send size={15} />
            </button>
          </form>
        )}
      </div>

      <AnimatePresence>
        {popup && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/40 px-4">
            <OSWindow id="gotmail" title="you've got mail!" tint="lavender" isStatic onClose={() => setPopup(false)} className="w-full max-w-sm">
              <div className="flex flex-col items-center p-7 text-center">
                <span className="flex h-14 w-14 items-center justify-center border-[3px] border-ink bg-white shadow-hard-sm">
                  <Mail size={24} />
                </span>
                <p data-testid="gotmail-text" className="mt-5 font-spacemono text-sm font-bold">{intro}</p>
                <button data-testid="gotmail-ok" onClick={() => setPopup(false)} className="btn-chunky mt-6 bg-mint px-8 py-2.5 text-xs">
                  OK
                </button>
              </div>
            </OSWindow>
          </div>
        )}
      </AnimatePresence>
    </OSChrome>
  );
}
